import { get } from '@ember/object';
import { concat } from '@ember/helper';
import { currentNonPartnerEmployerApproval } from 'tio-common/validation-schema/forms/pslf-approver';
import { flatten, safeParse } from 'valibot';
import { fn } from '@ember/helper';
import { trackedFunction } from 'reactiveweb/function';
import { service } from '@ember/service';
import { stateOptions } from 'tio-common/components/strategy-finder/strategy-finder-form';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Popover from 'tio-reimagined/components/popover';
import awsUrl from 'tio-employee/helpers/aws-url';
import Component from '@glimmer/component';
import errorsForField from 'tio-common/helpers/errors-for-field';
import { Input, RadioGroup, Select } from 'tio-ui/components/forms';
import type RouterService from '@ember/routing/router-service';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { Checkbox } from 'tio-ui/components/forms';

type NonPartnerVerifyResponse = {
  'approver-email': string;
  'borrower-form-type': string;
  'borrower-name': string;
  'borrower-sign-date': string;
  'employer-sign-date': string | undefined;
  'requires-employer-signature': boolean;
  'company-name': string;
  'company-website': string;
  'borrower-employment-start-date': string;
  'borrower-employment-end-date': string;
  'borrower-employment-continues': boolean;
  'borrower-employment-hours': string;
  'borrower-employment-status': string;
  status: string;
};

interface S {
  Args: {
    model: {
      documentId: string;
      initialValues: NonPartnerVerifyResponse;
      approvalId: string;
    };
  };
  Element: HTMLElement;
}

export default class PslfNonPartnerVerificationRoute extends Component<S> {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @tracked showForm: boolean = false;
  @tracked showEmbed: boolean = false;
  @tracked currentEmployee: boolean = false;
  @tracked employmentStartDate: string = '';
  @tracked employmentEndDate: string = '';
  @tracked weeklyHours: string = '';
  @tracked employmentStatus: string = '';
  @tracked employerWebsite: string = '';
  @tracked employerName: string = '';
  @tracked employerEin: string = '';
  @tracked addressLine1: string = '';
  @tracked addressLine2: string = '';
  @tracked employerState: string = '';
  @tracked selectedState: string = '';
  @tracked city: string = '';
  @tracked postalCode: string = '';
  @tracked hasSubmitted: boolean = false;
  @tracked loading = false;

  get states() {
    return stateOptions.map((state) => ({
      label: state.name,
      key: state.abbr,
    }));
  }

  constructor(owner: Owner, args: S['Args']) {
    super(owner, args);
    this.employerName = this.args.model.initialValues['company-name'];
    this.employerWebsite = this.args.model.initialValues['company-website'];
    this.employmentStartDate = this.args.model.initialValues['borrower-employment-start-date'];
    this.employmentEndDate = this.args.model.initialValues['borrower-employment-end-date'];
    this.weeklyHours = this.args.model.initialValues['borrower-employment-hours'];
    this.employmentStatus = this.args.model.initialValues['borrower-employment-status'];
  }

  get formInfo() {
    return this.args.model.initialValues;
  }
  get borrowerName() {
    return this.formInfo['borrower-name'];
  }
  get borrowerSignDate() {
    return this.formInfo['borrower-sign-date'];
  }
  get approverEmail() {
    return this.formInfo['approver-email'];
  }

  get approverSignatureNeeded() {
    return this.formInfo.status === 'borrower_signed';
  }

  setState = (value: string) => {
    this.employerState = value;
    this.selectedState = value;
  };

  toggleShowForm = () => {
    this.showForm = !this.showForm;
  };

  get logo() {
    return awsUrl(`tuition.png`);
  }

  get formModel() {
    return {
      approvalId: this.args.model.approvalId,
      employerApartment: this.addressLine2,
      employerCity: this.city,
      employerEin: this.employerEin,
      employerName: this.employerName,
      employerState: this.employerState,
      employerStreetAddress: this.addressLine1,
      employerWebsite: this.employerWebsite,
      employerZip: this.postalCode,
      employmentContinues: this.currentEmployee,
      employmentEndDate: this.employmentEndDate,
      employmentHours: this.weeklyHours,
      employmentStartDate: this.employmentStartDate,
      employmentStatus: this.employmentStatus,
      id: this.args.model.documentId,
    };
  }

  get formValidationErrors() {
    const schema = currentNonPartnerEmployerApproval(this.currentEmployee);
    const result = safeParse(schema, this.formModel, { abortEarly: false });
    if (!result.success) {
      const flattened = flatten(result.issues);
      // Convert to Yup-style ValidationError format for errorsForField helper
      return Object.entries(flattened.nested ?? {}).map(([path, messages]) => ({
        path,
        errors: messages || [],
        value: '',
      }));
    }
    return [];
  }

  embedUrlData = trackedFunction(this, async () => {
    const response = await this.store
      .adapterFor('pslf-form')
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      .prepareForNonPartnerEmployerSignature(this.args.model.approvalId, this.formModel);
    return response.embedUrl;
  });

  get embedUrl() {
    return this.embedUrlData.value || '';
  }

  submit = () => {
    this.hasSubmitted = true;
    const errors = this.formValidationErrors;
    if (errors.length) {
      console.log('Form errors: ', errors);
      return;
    } else {
      this.loading = true;
      this.showEmbed = true;
      this.showForm = false;
      this.loading = false;
      return;
    }
  };

  <template>
    <div class="flex antialiased bg-white text-black overflow-hidden h-screen w-screen">
      <div class="flex flex-col grow h-full overflow-auto scroll-smooth">
        <div class="grid grid-cols-6 gap-4">
          {{#if this.approverSignatureNeeded}}
            {{#if this.showForm}}
              <div class="col-start-2 col-span-4 grid mt-10 mb-24">
                <form ...attributes>
                  <p class="text-lg font-semibold">
                    {{concat this.borrowerName "'s " (t "common.pslf.employment_dates")}}</p>
                  <Checkbox
                    @label="Still Employed"
                    @checked={{this.currentEmployee}}
                    @onChange={{fn (mut this.currentEmployee)}}
                    class="py-4"
                  />
                  <Input
                    name="employmentStartDate"
                    type="date"
                    @label={{t "pslf.workflow.start_date"}}
                    @errors={{errorsForField
                      "employmentStartDate"
                      schemaErrors=this.formValidationErrors
                    }}
                    @value={{this.employmentStartDate}}
                    @onInput={{fn (mut this.employmentStartDate)}}
                  />
                  {{#unless this.currentEmployee}}
                    <Input
                      name="employmentEndDate"
                      type="date"
                      @label={{t "pslf.workflow.end_date"}}
                      @errors={{errorsForField
                        "employmentEndDate"
                        schemaErrors=this.formValidationErrors
                      }}
                      @value={{this.employmentEndDate}}
                      @onInput={{fn (mut this.employmentEndDate)}}
                    />
                  {{/unless}}

                  {{! WEEKLY HOURS }}
                  <p class="text-lg font-semibold pt-4 pb-2">
                    {{t "pslf.employer_workflow.average_hours_per_week"}}
                  </p>
                  <p>{{t "pslf.employer_workflow.hours_sub" htmlSafe=true}}</p>
                  <Input
                    name="weeklyHours"
                    @type="number"
                    @errors={{errorsForField "weeklyHours" schemaErrors=this.formValidationErrors}}
                    @value={{this.weeklyHours}}
                    @onInput={{fn (mut this.weeklyHours)}}
                  />
                  {{! FULL OR PART TIME }}
                  <div class="flex flex-row items-center">
                    <Popover @triggerMode="hover">
                      <:trigger as |pop|>
                        <span {{pop.register}} {{pop.hoverHandlers}}>
                          <p class="text-lg font-semibold">
                            {{t
                              "pslf.employer_workflow.employment_status_current"
                              name=this.borrowerName
                            }}
                          </p>
                        </span>
                      </:trigger>
                      <:body>
                        {{t "pslf.employer_workflow.full_time_means"}}
                      </:body>
                    </Popover>
                  </div>
                  <RadioGroup
                    @name="employmentStatus"
                    @value={{this.employmentStatus}}
                    @onChange={{fn (mut this.employmentStatus)}}
                    @errors={{errorsForField
                      "employmentStatus"
                      schemaErrors=this.formValidationErrors
                    }}
                    as |Radio|
                  >
                    <Radio @value="fullTime" @label={{t "common.full_time"}} />
                    <Radio @value="partTime" @label={{t "common.part_time"}} />
                  </RadioGroup>
                  {{! COMPANY NAME }}
                  <p class="text-lg font-semibold pt-4 pb-2">
                    {{t "pslf.approver_email_landing_page.what_is_org_name"}}</p>
                  <p class="py-2">{{t "pslf.employer_workflow.employer_name_blurb"}}</p>
                  <Input
                    name="employerName"
                    @value={{this.employerName}}
                    @errors={{errorsForField "employerName" schemaErrors=this.formValidationErrors}}
                    @onInput={{fn (mut this.employerName)}}
                  />
                  <p class="text-lg font-semibold pt-4 pb-2">
                    {{t "pslf.approver_email_landing_page.what_is_org_site"}}</p>
                  <Input
                    name="employerWebsite"
                    @value={{this.employerWebsite}}
                    @errors={{errorsForField
                      "employerWebsite"
                      schemaErrors=this.formValidationErrors
                    }}
                    @onInput={{fn (mut this.employerWebsite)}}
                  />

                  <Input
                    name="employerEin"
                    @errors={{errorsForField "employerEin" schemaErrors=this.formValidationErrors}}
                    @label={{t "pslf.employer_workflow.what_is_this_orgs_ein"}}
                    @onInput={{fn (mut this.employerEin)}}
                    @value={{this.employerEin}}
                    required={{true}}
                  />
                  <Input
                    name="addressLine1"
                    @errors={{errorsForField
                      "employerStreetAddress"
                      schemaErrors=this.formValidationErrors
                    }}
                    @label="Address Line 1"
                    @onInput={{fn (mut this.addressLine1)}}
                    @value={{this.addressLine1}}
                  />
                  <Input
                    @errors={{errorsForField
                      "employerApartment"
                      schemaErrors=this.formValidationErrors
                    }}
                    @label="Address Line 2"
                    @onInput={{fn (mut this.addressLine2)}}
                    @value={{this.addressLine2}}
                  />
                  <Input
                    name="city"
                    @errors={{errorsForField "employerCity" schemaErrors=this.formValidationErrors}}
                    @label="City"
                    @onInput={{fn (mut this.city)}}
                    @value={{this.city}}
                  />
                  <Select
                    @name="state"
                    @errors={{errorsForField
                      "employerState"
                      schemaErrors=this.formValidationErrors
                    }}
                    @label="State"
                    @selectionMode="single"
                    @onAction={{this.setState}}
                    @items={{this.states}}
                    @isFilterable={{true}}
                    @selectedKey={{this.selectedState}}
                    @isRequired={{true}}
                  />
                  <Input
                    name="postalCode"
                    @label="Zip Code"
                    @errors={{errorsForField "employerZip" schemaErrors=this.formValidationErrors}}
                    @onInput={{fn (mut this.postalCode)}}
                    @value={{this.postalCode}}
                  />
                  <Button
                    @intent="primary"
                    class="w-52 mx-auto visited:text-white my-4"
                    {{on "click" this.submit}}
                    @isRunning={{this.loading}}
                  >
                    {{t "common.submit"}}
                  </Button>
                </form>
              </div>
            {{else}}
              <div class="col-start-2 col-span-4 mb-10 grid">
                <h2 class="font-semibold text-2xl my-4">{{t "pslf.default_full"}}</h2>
                <h1 class="mb-6">{{t
                    "pslf.approver_email_landing_page.sent_to_by"
                    borrowerName=this.borrowerName
                    dateSent=(safeFormatDate this.borrowerSignDate)
                    email=this.approverEmail
                  }}
                </h1>
                <p class="text-xl mb-4">{{t
                    "pslf.approver_email_landing_page.explanation"
                    employerSignDate=(get @model.initialValues "employer-sign-date")
                    borrowerName=this.borrowerName
                  }}</p>
                <p class="text-xl mb-4">{{t
                    "pslf.approver_email_landing_page.directive"
                    companyName=(get @model.initialValues "company-name")
                    borrowerName=this.borrowerName
                  }}</p>
                <p class="text-xl mb-4">{{t
                    "pslf.approver_email_landing_page.if_in_error"
                    htmlSafe=true
                  }}</p>

                <Button
                  @intent="primary"
                  {{on "click" this.toggleShowForm}}
                  class="my-8 justify-self-center"
                >
                  {{t "pslf.approver_email_landing_page.view_form"}}
                </Button>
              </div>
            {{/if}}
          {{else}}
            <div class="col-start-2 col-span-4 grid mt-10 mb-24">
              <div id="header-title" class="flex flex-col header-left grow mb-10" ...attributes>
                <img
                  src={{this.logo}}
                  class="h-10 max-w-32 object-contain"
                  alt={{t "tuition_io_lowercase"}}
                  title={{t "tuition_io_lowercase"}}
                />
                <div class="text-2xs text-gray-600 mt-1">
                  {{t
                    "header.banner.tagline"
                    programTitle="education assistance benefits"
                    brandClass="text-purple-900 font-semibold"
                    htmlSafe=true
                  }}
                </div>
              </div>
              <p class="text-xl m-10">{{t "pslf.form_no_longer_awaiting_approval"}}</p>
            </div>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
